import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  OnInit,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { finalize } from 'rxjs';
import moment from 'moment';
import {
  EMPLOYEE_ROUTES,
  RequestService,
  OverlayLoaderComponent,
} from '../../../../common';
import {
  CreateRequestSchedulePayload,
  RequestScheduleDetails,
  RequestScheduleType,
  ScheduleRecurrenceBy,
  ScheduleRecurrenceDetails,
} from '../../../../common/models/request-schedule.model';
import {
  SCHEDULE_TYPE_OPTIONS,
  WEEK_DAYS,
} from './schedule-workflow.constants';
import {
  buildLegacyMonthlyRepeatOnOptions,
  buildLegacyYearlyRepeatOnOptions,
  prepareScheduleRecurrencePayload,
} from './schedule-workflow.utils';
import {
  RepeatOnOptionValue,
  mapRepeatOnFromApi,
} from '../../../../common/utils/recurrence.utils';
import { ScheduleRecurrenceFieldsComponent } from './schedule-recurrence-fields/schedule-recurrence-fields.component';
import { FeatureFlagsService } from '../../../../common/services/feature-flags/feature-flags.service';
import { FEATURE_FLAGS } from '../../../../common/constants/feature-flags.constants';
import { ToasterService } from '../../../../../ui-components/services/toast/toast.service';
import { ChevronDownIconComponent } from '@kuiper/icons';
import { DatePickerComponent } from '../../../../../ui-components/lib/date-picker/date-picker.component';
import { DateFormat } from '../../../../../ui-components/lib/date-picker/date-picker.constant';
import { InputComponent } from '../../../../../ui-components/lib/input/input.component';
import { SelectComponent } from '../../../../../ui-components/lib/select/select.component';
import { MultiSelectComponent } from '../../../../../ui-components/lib/multi-select/multi-select.component';
import { ButtonComponent } from '../../../../../ui-components/lib/button/button.component';
import { ValidationMessageComponent } from '../../../../../ui-components/lib/validation-message/validation-message.component';
import { OptionItem } from '../../../../../ui-components/lib/option.interface';
import { RichEditorDualLangTextAreaComponent } from '../../../../../ui-components/lib/rich-editor-dual-lang-text-area/rich-editor-dual-lang-text-area.component';
import { FormService } from '../../../../../form-builder/services/form/form.service';

@Component({
  selector: 'lib-schedule-workflow',
  imports: [
    ReactiveFormsModule,
    TranslocoPipe,
    ChevronDownIconComponent,
    DatePickerComponent,
    InputComponent,
    SelectComponent,
    MultiSelectComponent,
    ButtonComponent,
    ValidationMessageComponent,
    RichEditorDualLangTextAreaComponent,
    OverlayLoaderComponent,
    ScheduleRecurrenceFieldsComponent,
  ],
  templateUrl: './schedule-workflow.component.html',
  styleUrl: './schedule-workflow.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class ScheduleWorkflowComponent implements OnInit {
  readonly EMPLOYEE_ROUTES = EMPLOYEE_ROUTES;

  private readonly formBuilder = inject(FormBuilder);
  private readonly requestService = inject(RequestService);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  private readonly toaster = inject(ToasterService);
  private readonly formService = inject(FormService);
  private readonly featureFlagsService = inject(FeatureFlagsService);

  isLoading = signal<boolean>(false);
  isSubmitting = signal<boolean>(false);
  requestId = signal<string>(this.activatedRoute.snapshot.params['requestId']);
  scheduleId = signal<string>(
    this.activatedRoute.snapshot.params['scheduleId']
  );

  readonly isScheduleOnceOnlyEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.schedule_a_workflow_to_run_once
    )
  );

  readonly isAllScheduleTypesEnabled = signal(
    this.featureFlagsService.getFeatureFlagValue(
      FEATURE_FLAGS.WORKFLOWS.schedule_workflow_all_type
    )
  );

  readonly weekDays = WEEK_DAYS;
  readonly minStartDateTime = signal(new Date(Date.now() - 1000 * 60));
  readonly minEndDateTime = signal<Date | undefined>(undefined);

  form = this.formBuilder.group({
    start_at: this.formBuilder.control<string>('', {
      validators: [Validators.required],
      nonNullable: true,
    }),
    schedule_type: this.formBuilder.control<RequestScheduleType | null>(null, {
      validators: [Validators.required],
    }),
    description: this.formBuilder.control<string>('', {
      validators: [Validators.maxLength(3000)],
      nonNullable: true,
    }),
    repeat_every: this.formBuilder.control<number | null>(null),
    end_at: this.formBuilder.control<string | null>(null),
    repeat_days: this.formBuilder.control<string[]>([], { nonNullable: true }),
    repeat_on_by_day_of_month: this.formBuilder.control<boolean | null>(null),
    recurrence: this.formBuilder.group({
      interval: this.formBuilder.control<number | null>(null),
      by: this.formBuilder.control<string>(ScheduleRecurrenceBy.INTERVAL, {
        nonNullable: true,
      }),
      months: this.formBuilder.control<number[]>([], { nonNullable: true }),
      days_of_week: this.formBuilder.control<number[]>([], {
        nonNullable: true,
      }),
      repeatOnByDayOfMonth:
        this.formBuilder.control<RepeatOnOptionValue | null>(null),
    }),
  });

  scheduleTypeSignal = toSignal(this.form.controls.schedule_type.valueChanges, {
    initialValue: this.form.controls.schedule_type.value,
  });
  startDateTimeSignal = toSignal(this.form.controls.start_at.valueChanges, {
    initialValue: this.form.controls.start_at.value,
  });

  repeatOnOptions = computed(() => {
    const value = this.startDateTimeSignal();
    const scheduleType = this.scheduleTypeSignal();

    if (!value) {
      return [] as OptionItem[];
    }

    const startDate = this.parseFormDateToDate(value);
    if (!startDate) {
      return [] as OptionItem[];
    }

    const translate = (key: string, params?: Record<string, string | number>) =>
      this.transloco.translate(key, params);

    if (scheduleType === RequestScheduleType.MONTHLY) {
      return buildLegacyMonthlyRepeatOnOptions(
        startDate,
        translate
      ) as OptionItem[];
    }

    if (scheduleType === RequestScheduleType.YEARLY) {
      return buildLegacyYearlyRepeatOnOptions(
        startDate,
        translate
      ) as OptionItem[];
    }

    return [] as OptionItem[];
  });

  get translatedScheduleTypeOptions(): OptionItem[] {
    const isOneTimeOnly =
      this.isScheduleOnceOnlyEnabled() && !this.isAllScheduleTypesEnabled();

    const options = isOneTimeOnly
      ? SCHEDULE_TYPE_OPTIONS.filter(
          opt => opt.value === RequestScheduleType.ONE_TIME
        )
      : SCHEDULE_TYPE_OPTIONS;

    return options.map(opt => ({
      value: opt.value,
      label: this.transloco.translate(opt.label),
    }));
  }

  get translatedWeekDayOptions(): OptionItem[] {
    return WEEK_DAYS.map(day => ({
      value: day.value,
      label: this.transloco.translate(
        `schedule_workflow_week_day_${day.value}_label`
      ),
    }));
  }

  get repeatEveryUnit(): string {
    const unitMap: Record<string, string> = {
      [RequestScheduleType.DAILY]: this.transloco.translate(
        'schedule_workflow_unit_days'
      ),
      [RequestScheduleType.WEEKLY]: this.transloco.translate(
        'schedule_workflow_unit_weeks'
      ),
      [RequestScheduleType.MONTHLY]: this.transloco.translate(
        'schedule_workflow_unit_months'
      ),
      [RequestScheduleType.YEARLY]: this.transloco.translate(
        'schedule_workflow_unit_years'
      ),
    };
    return unitMap[this.form.controls.schedule_type.value ?? ''] || '';
  }

  constructor() {
    effect(() => {
      const scheduleType = this.scheduleTypeSignal();
      if (scheduleType) {
        this.applyTypeValidators(scheduleType);
      }
    });

    this.form.controls.start_at.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value) {
          const startDate = this.parseFormDateToDate(value);
          if (startDate) {
            this.minEndDateTime.set(startDate);
            this.validateEndDateAgainstStart(startDate);
          }
        } else {
          this.minEndDateTime.set(undefined);
        }
      });
  }

  ngOnInit(): void {
    if (this.requestId() && this.scheduleId()) {
      this.loadScheduleDetails();
    }
  }

  private loadScheduleDetails() {
    this.isLoading.set(true);
    this.requestService
      .getRequestScheduleById(this.requestId(), this.scheduleId())
      .pipe(
        finalize(() => this.isLoading.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(response => {
        const details = response.data;

        this.form.patchValue({
          start_at: details.start_at,
          schedule_type: this.resolveScheduleType(details),
          description: details.description || '',
          repeat_every: details.repeat_every,
          end_at: details.end_at || null,
          repeat_days: details.repeat_days || [],
          repeat_on_by_day_of_month: details.repeat_on_by_day_of_month,
          ...(this.isAllScheduleTypesEnabled() && details.recurrence
            ? { recurrence: this.mapRecurrenceFormValue(details.recurrence) }
            : {}),
        });
      });
  }

  /** Recurring schedules carry the daily/weekly/monthly/yearly value inside `recurrence`. */
  private resolveScheduleType(
    details: RequestScheduleDetails
  ): RequestScheduleType {
    const scheduleType = details.schedule_type?.value as RequestScheduleType;

    if (scheduleType !== RequestScheduleType.RECURRING) {
      return scheduleType;
    }

    return (
      (this.resolveOptionValue(details.recurrence?.type) as
        | RequestScheduleType
        | undefined) ?? RequestScheduleType.ONE_TIME
    );
  }

  /**
   * Edit-mode recurrence enums (`type`, `by`, `mode`) come back as `{ label, value }`.
   * The form stores the raw `value` only.
   */
  private resolveOptionValue(
    option: { value?: string | null } | string | null | undefined
  ): string | undefined {
    if (typeof option === 'string') {
      return option || undefined;
    }

    return option?.value ?? undefined;
  }

  private mapRecurrenceFormValue(recurrence: ScheduleRecurrenceDetails) {
    return {
      interval: recurrence.interval ?? null,
      by: this.resolveOptionValue(recurrence.by) ?? ScheduleRecurrenceBy.INTERVAL,
      months: recurrence.months ?? [],
      days_of_week: recurrence.days_of_week ?? [],
      repeatOnByDayOfMonth: mapRepeatOnFromApi(
        this.resolveOptionValue(recurrence.mode),
        recurrence.relative_option,
        recurrence.days_of_month
      ),
    };
  }

  get isOneTime(): boolean {
    const scheduleType = this.form.controls.schedule_type.value;
    return !scheduleType || scheduleType === RequestScheduleType.ONE_TIME;
  }

  get isWeekly(): boolean {
    return (
      this.form.controls.schedule_type.value === RequestScheduleType.WEEKLY
    );
  }

  get isMonthly(): boolean {
    return (
      this.form.controls.schedule_type.value === RequestScheduleType.MONTHLY
    );
  }

  get isYearly(): boolean {
    return (
      this.form.controls.schedule_type.value === RequestScheduleType.YEARLY
    );
  }

  saveSchedule() {
    if (this.form.invalid || !this.requestId()) {
      this.form.markAllAsTouched();
      return;
    }

    const payload = this.buildPayload();

    this.isSubmitting.set(true);

    const requestSchedule = this.scheduleId()
      ? this.requestService.updateRequestSchedule(
          this.requestId(),
          this.scheduleId(),
          payload
        )
      : this.requestService.createRequestSchedule(this.requestId(), payload);

    requestSchedule
      .pipe(
        finalize(() => this.isSubmitting.set(false)),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe({
        next: () => {
          this.toaster.add(
            this.transloco.translate('schedule_workflow_success_message'),
            '',
            '',
            'success'
          );
          this.router.navigate([
            this.EMPLOYEE_ROUTES.REQUESTS.DETAILS,
            this.requestId(),
          ]);
        },
        error: error => {
          this.formService.addControlErrors(error, this.form);
        },
      });
  }

  goBack() {
    history.back();
  }

  private applyTypeValidators(type: RequestScheduleType) {
    this.applyEndDateValidators(type);

    if (this.isAllScheduleTypesEnabled()) {
      // Recurrence validators are owned by lib-schedule-recurrence-fields.
      this.clearLegacyRepetitionFields();
      return;
    }

    this.applyLegacyRepetitionValidators(type);
  }

  private applyEndDateValidators(type: RequestScheduleType) {
    const endDateTimeControl = this.form.controls.end_at;
    const needsRepetitionFields = type !== RequestScheduleType.ONE_TIME;

    endDateTimeControl.setValidators(
      needsRepetitionFields ? [Validators.required] : []
    );

    if (!needsRepetitionFields) {
      endDateTimeControl.setValue(null, { emitEvent: false });
    }

    endDateTimeControl.updateValueAndValidity({ emitEvent: false });
  }

  private clearLegacyRepetitionFields() {
    const repeatEveryControl = this.form.controls.repeat_every;
    const daysControl = this.form.controls.repeat_days;
    const repeatOnControl = this.form.controls.repeat_on_by_day_of_month;

    repeatEveryControl.setValidators([]);
    daysControl.setValidators([]);
    repeatOnControl.setValidators([]);

    repeatEveryControl.setValue(null, { emitEvent: false });
    daysControl.setValue([], { emitEvent: false });
    repeatOnControl.setValue(null, { emitEvent: false });

    repeatEveryControl.updateValueAndValidity({ emitEvent: false });
    daysControl.updateValueAndValidity({ emitEvent: false });
    repeatOnControl.updateValueAndValidity({ emitEvent: false });
  }

  private applyLegacyRepetitionValidators(type: RequestScheduleType) {
    const repeatEveryControl = this.form.controls.repeat_every;
    const daysControl = this.form.controls.repeat_days;
    const repeatOnControl = this.form.controls.repeat_on_by_day_of_month;

    const needsRepetitionFields = type !== RequestScheduleType.ONE_TIME;

    repeatEveryControl.setValidators(
      needsRepetitionFields ? [Validators.required, Validators.min(1)] : []
    );

    daysControl.setValidators(
      type === RequestScheduleType.WEEKLY ? [this.arrayRequiredValidator] : []
    );

    repeatOnControl.setValidators(
      type === RequestScheduleType.MONTHLY ||
        type === RequestScheduleType.YEARLY
        ? [Validators.required]
        : []
    );

    if (!needsRepetitionFields) {
      repeatEveryControl.setValue(null, { emitEvent: false });
      daysControl.setValue([], { emitEvent: false });
      repeatOnControl.setValue(null, { emitEvent: false });
    }

    if (type !== RequestScheduleType.WEEKLY) {
      daysControl.setValue([], { emitEvent: false });
    }

    if (
      type !== RequestScheduleType.MONTHLY &&
      type !== RequestScheduleType.YEARLY
    ) {
      repeatOnControl.setValue(null, { emitEvent: false });
    }

    repeatEveryControl.updateValueAndValidity({ emitEvent: false });
    daysControl.updateValueAndValidity({ emitEvent: false });
    repeatOnControl.updateValueAndValidity({ emitEvent: false });
  }

  private buildPayload(): CreateRequestSchedulePayload {
    const formValue = this.form.getRawValue();
    const scheduleType = formValue.schedule_type as RequestScheduleType;

    const payload: CreateRequestSchedulePayload = {
      start_at: this.parseDateValue(formValue.start_at),
      schedule_type: scheduleType,
      description: formValue.description || '',
    };

    if (this.isAllScheduleTypesEnabled()) {
      if (this.isOneTime) {
        return payload;
      }

      payload.schedule_type = RequestScheduleType.RECURRING;
      payload.end_at = this.parseDateValue(formValue.end_at || '');
      payload.recurrence = prepareScheduleRecurrencePayload(
        scheduleType,
        formValue.recurrence,
        this.parseFormDateToDate(formValue.start_at)
      );

      return payload;
    }

    if (scheduleType !== RequestScheduleType.ONE_TIME) {
      payload.repeat_every = Number(formValue.repeat_every || 1);
      payload.end_at = this.parseDateValue(formValue.end_at || '');
    }

    if (scheduleType === RequestScheduleType.WEEKLY) {
      payload.repeat_days = formValue.repeat_days;
    }

    if (
      scheduleType === RequestScheduleType.MONTHLY ||
      scheduleType === RequestScheduleType.YEARLY
    ) {
      payload.repeat_on_by_day_of_month = Boolean(
        formValue.repeat_on_by_day_of_month
      );
    }

    return payload;
  }

  private parseDateValue(value: string): string {
    if (!value) return '';
    const m = moment.utc(this.getDatePickerValue(value), DateFormat, true);
    return m.isValid()
      ? m.format('YYYY-MM-DD HH:mm:ss')
      : moment(value).utc().format('YYYY-MM-DD HH:mm:ss');
  }

  private parseFormDateToDate(value: string): Date | undefined {
    if (!value) return undefined;
    const m = moment.utc(this.getDatePickerValue(value), DateFormat, true);
    return m.isValid() ? m.toDate() : undefined;
  }

  private getDatePickerValue(value: string): string {
    return value.includes(' - (')
      ? value.split(' - (')[0].trim()
      : value.trim();
  }

  private validateEndDateAgainstStart(startDate: Date): void {
    const endControl = this.form.controls.end_at;
    const endValue = endControl.value;
    if (!endValue) return;

    const endDate = this.parseFormDateToDate(endValue);
    if (endDate && endDate.getTime() < startDate.getTime()) {
      endControl.setErrors({
        minDate: {
          minDate: true,
          date: moment(startDate).format(DateFormat),
        },
      });
      return;
    }

    if (endControl.hasError('minDate')) {
      const remainingErrors = { ...endControl.errors };
      delete remainingErrors['minDate'];
      endControl.setErrors(
        Object.keys(remainingErrors).length ? remainingErrors : null
      );
    }
  }

  private arrayRequiredValidator(control: AbstractControl) {
    const value = control.value as string[] | null;
    return value?.length ? null : { required: true };
  }
}
